import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { Box } from "@mui/material";
import {
  myDocumentsUserListMobileTableDef,
  myDocumentsUserListTableDef,
} from "my-documents/configs";
import { useMyDocumentsUsers } from "my-documents/hooks";
import {
  OcCard,
  OcDataTable,
  OcPageNameTitle,
  OcPageTransition,
  OcRoleFilter,
} from "oc/components";
import { ProductsT, RoleCategoriesT } from "oc/configs/constants";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcRoleFilters,
} from "oc/hooks";

export const MyDocumentsViewDocumentsPage = ({
  product = "MyDocuments",
  role = "viewer",
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  let tableId = "myDocumentsViewerTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });

  const [filterable, setFilterable] = useState(false);

  let { contractCode: contract } = useOcCurrentUserState();

  const handleCompleted = () => {
    handleChangeFilterable(false);
  };

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract: contract,
    product: product,
    role: role,
  });

  const { loadQuery, data, loading } = useMyDocumentsUsers({
    roleFilters,
    filterable,
    tableData,
    role: role,
    onCompleted: handleCompleted,
  });

  const handleClearFilters = () => {
    resetRoleFilters();
  };

  const handleChangeFilterable = (state: boolean) => {
    setFilterable(state);
  };

  const buttonDisable = _.isEqual(roleFilters, prevRoleFilters);

  const handleLoadQuery = () => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
  };

  const [prevTableData, setPrevTableData] = useOcTableState(
    `${tableId}Prev`,
    initialTableData,
  );

  const list = {
    rows: data?.rows,
    total: data?.total,
  };

  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(
    undefined,
  );
  const originalonSelectRow = (o: any) => {
    if (isMobile) {
      history.push(`/my-documents/user-files/${o?.row?.employee}/${o?.row?.contract}`);
    } else {
      history.push(`/my-documents/user-files/${o.employee}/${o.contract}`);
    }
  };
  const onSelectRow = (o: {
    employee: string;
    contract: string;
    _id?: string;
  }) => {
    if (isMobile) {
      setSelectedRowId(
        selectedRowId === o?._id ? undefined : (o?._id ?? undefined),
      );
    } else {
      originalonSelectRow(o);
    }
  };

  const handleTableDataChange = (state: typeof initialTableData) => {
    const keys = Object.keys(state).toString();
    setTableData(state);
    if (
      keys === "searchField" ||
      (keys === "searchText" && state?.searchText?.length < 3)
    ) {
      return;
    }
    loadQuery();
  };

  const tableDef = isMobile
    ? myDocumentsUserListMobileTableDef
    : myDocumentsUserListTableDef;

  const filteredTableDef = {
    id: myDocumentsUserListTableDef?.id,
    columns: myDocumentsUserListTableDef?.columns?.filter(
      (column) =>
        !myDocumentsUserListMobileTableDef?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  // Initial load
  useEffect(() => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
  }, []); // eslint-disable-line

  // Handle tableData changes
  useEffect(() => {
    const isTabledataChanged = !_.isEqual(tableData, prevTableData);
    if (isTabledataChanged) {
      handleLoadQuery();
      setPrevTableData(tableData);
    }
  }, [tableData]); // eslint-disable-line

  const handlePrimaryButton = () => {
    handleLoadQuery();
    handleChangeFilterable(true);
  };

  return (
    <OcPageTransition>
      <OcPageNameTitle
        title={t("MY_DOCUMENTS_VIEWING_DOCUMENTS")}
        goBack={false}
      />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        <Box>
          <OcCard label={t("MY_DOCUMENTS_VIEWING_DOCUMENTS")} loading={loading}>
            <OcRoleFilter
              subtypeProps={{
                roleFilters,
                handleAddItem: handleAddFilter,
                handleRemoveItem: handleRemoveFilter,
                product: product as ProductsT,
                role: role as RoleCategoriesT,
                queryType: "notAdmin",
                isCheckbox: true,
              }}
              buttonBarProps={{
                handlePrimaryButton,
                handleRightButton: handleClearFilters,
                primaryButtonDisabled: buttonDisable,
              }}
              summaryItems={[
                {
                  label: "OC_TOTAL_COUNT_OF_EMPLOYEES",
                  value: data?.allUsersCount || 0,
                },
              ]}
            />
            <Box paddingTop="32px">
              <OcDataTable
                tableDef={tableDef}
                tableId={tableId}
                tableData={tableData}
                setTableData={handleTableDataChange}
                data={list}
                loading={loading}
                onSelectRow={onSelectRow}
                pagination={true}
                filteredTableDef={filteredTableDef}
                selectedRowId={selectedRowId}
                handleMoreButton={originalonSelectRow}
                isNotExaminer={false}
              />
            </Box>
          </OcCard>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyDocumentsViewDocumentsPage;
